<template>
  <a-drawer
      title="脱离数据详情"
      :width="'70%'"
      :open="open"
      :closable="true"
      @close="handleClose"
      placement="right"
  >
    <div class="drawer-content">
      <div class="new-main-layout">
        <!-- 内容区域 -->
        <div class="content-section">
          <div class="main-section">
            <div class="map-section">
              <!-- 修改为 antd 的单选按钮组 -->
              <div class="radio-group-container">
                <a-radio-group v-model:value="selectedCategory" size="small">
                  <a-radio-button value="departureType">脱离类别</a-radio-button>
                  <a-radio-button value="sceneCategory">脱离时场景分类</a-radio-button>
                  <a-radio-button value="participantType">交通参与者类型</a-radio-button>
                  <a-radio-button value="facilityType">主要交通设施</a-radio-button>
                  <a-radio-button value="departureReason">脱离原因</a-radio-button>
                  <a-radio-button value="interventionType">人工介入方式</a-radio-button>
                </a-radio-group>
              </div>
              <div id="departure-map-container" class="map-container"></div>
            </div>

            <div class="right-section">
              <!-- 第一行：脱离事件总数统计 -->
              <div class="total-stats">
                <div class="total-count">
                  <span class="count-number">{{ totalDepartureCount }}</span>
                  <span class="count-label">总脱离事件数</span>
                </div>
              </div>

              <!-- 第二行：根据选中的值显示对应的饼状图 -->
              <div class="chart-item-large">
                <div ref="selectedCategoryChart" class="chart"></div>
                <h3 class="chart-title">{{ getSelectedCategoryTitle() }}</h3>
              </div>
            </div>
          </div>

          <!-- 所有饼状图数据区域 - 每行显示三个 -->
          <div class="all-charts-section">
            <div class="charts-grid">
              <!-- 脱离类别 -->
              <div class="chart-item-small">
                <div ref="departureTypeChart" class="chart"></div>
                <h4 class="chart-title-small">脱离类别</h4>
              </div>

              <!-- 脱离时场景分类 -->
              <div class="chart-item-small">
                <div ref="sceneCategoryChart" class="chart"></div>
                <h4 class="chart-title-small">脱离时场景分类</h4>
              </div>

              <!-- 交通参与者类型 -->
              <div class="chart-item-small">
                <div ref="participantTypeChart" class="chart"></div>
                <h4 class="chart-title-small">交通参与者类型</h4>
              </div>

              <!-- 主要交通设施 -->
              <div class="chart-item-small">
                <div ref="facilityTypeChart" class="chart"></div>
                <h4 class="chart-title-small">主要交通设施</h4>
              </div>

              <!-- 脱离原因 -->
              <div class="chart-item-small">
                <div ref="departureReasonChart" class="chart"></div>
                <h4 class="chart-title-small">脱离原因</h4>
              </div>

              <!-- 人工介入方式 -->
              <div class="chart-item-small">
                <div ref="interventionTypeChart" class="chart"></div>
                <h4 class="chart-title-small">人工介入方式</h4>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </a-drawer>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, computed, onBeforeUnmount } from 'vue'
import * as echarts from 'echarts'
import AMapLoader from '@amap/amap-jsapi-loader'
import { Drawer } from 'ant-design-vue'

// 接收父组件传递的参数
const props = defineProps({
  open: {
    type: Boolean,
    default: false
  },
  record: {
    type: Object,
    default: null
  },
  query: {
    type: Object,
    required: false,
    default: () => ({})
  }
})

// 定义事件
const emit = defineEmits(['close'])

// 处理关闭抽屉
const handleClose = () => {
  emit('close')
}

// 单选框选中的值
const selectedCategory = ref('departureType')

// 饼状图相关引用
const selectedCategoryChart = ref(null)
let selectedCategoryChartInstance = null

// 所有饼状图的引用
const departureTypeChart = ref(null)
const sceneCategoryChart = ref(null)
const participantTypeChart = ref(null)
const facilityTypeChart = ref(null)
const departureReasonChart = ref(null)
const interventionTypeChart = ref(null)

// 饼状图实例对象
let chartInstances = {}

// 地图实例引用
const mapInstance = ref(null)
const markers = ref([])

// 月份统计数据相关
const monthlyStats = ref([])
const selectedMonthId = ref(null)
const currentPage = ref(1)
const pageSize = ref(6) // 每页显示6个月

// 模拟数据
const chartData = {
  // 脱离时场景分类
  sceneCategory: [
    { name: '变更车道时', value: 15 },
    { name: '掉头时', value: 25 },
    { name: '直行时', value: 30 },
    { name: '路口左转弯时', value: 10 },
    { name: '路口右转弯时', value: 8 },
    { name: '遇到信号灯时', value: 7 },
    { name: '会车时', value: 4 },
    { name: '其他', value: 1 }
  ],

  // 脱离时主要交通参与者类型
  participantType: [
    { name: '大型客车', value: 35 },
    { name: '中大型货车', value: 25 },
    { name: '特种车', value: 15 },
    { name: '非机动车', value: 10 },
    { name: '行人', value: 10 },
    { name: '其他', value: 5 }
  ],

  // 脱离时主要交通设施
  facilityType: [
    { name: '锥桶', value: 20 },
    { name: '隔离护栏', value: 35 },
    { name: '施工带', value: 25 },
    { name: '其他', value: 20 }
  ],

  // 脱离类别
  departureType: [
    { name: '主动脱离', value: 45 },
    { name: '被动脱离', value: 55 }
  ],

  // 脱离原因
  departureReason: [
    { name: '系统故障', value: 25 },
    { name: '策略缺陷', value: 20 },
    { name: '人工安全防御', value: 15 },
    { name: '人为接管', value: 30 },
    { name: '其他', value: 10 }
  ],

  // 人工介入方式
  interventionType: [
    { name: '自动踏板', value: 30 },
    { name: '方向盘', value: 50 },
    { name: '硬或软开关介入', value: 20 }
  ]
}

// 模拟脱离点位数据 - 为每个点位添加分类属性以便着色
const departurePoints = ref([
  { id: 1, lng: 103.726465, lat: 36.107992, time: '2023-11-01 10:30:45', licensePlate: '测试牌照A12345', reportId: '1001', sceneCategory: '变更车道时', departureType: '主动脱离' },
  { id: 2, lng: 103.736465, lat: 36.117992, time: '2023-11-01 14:20:18', licensePlate: '测试牌照B67890', reportId: '1002', sceneCategory: '掉头时', departureType: '主动脱离' },
  { id: 3, lng: 103.716465, lat: 36.127992, time: '2023-11-02 09:15:30', licensePlate: '测试牌照C24680', reportId: '1003', sceneCategory: '直行时', departureType: '被动脱离' },
  { id: 4, lng: 103.746465, lat: 36.137992, time: '2023-11-02 16:45:50', licensePlate: '测试牌照D13579', reportId: '1004', sceneCategory: '其他', departureType: '其他' },
  { id: 5, lng: 103.706465, lat: 36.147992, time: '2023-11-03 11:20:15', licensePlate: '测试牌照E54321', reportId: '1005', sceneCategory: '遇到信号灯时', departureType: '其他' }
])

// 饼状图颜色配置
const chartColors = {
  // 脱离时场景分类
  sceneCategory: [
    {name: '变更车道时', color: '#52c41a'},
    {name: '掉头时', color: '#ff4d4f'},
    {name: '直行时', color: '#faad14'},
    {name: '路口左转弯时', color: '#1890ff'},
    {name: '路口右转弯时', color: '#722ed1'},
    {name: '遇到信号灯时', color: '#fa8c16'},
    {name: '会车时', color: '#13c2c2'},
    {name: '其他', color: '#eb2f96'}
  ],

  // 脱离时主要交通参与者类型
  participantType: [
    {name: '大型客车', color: '#a0d911'},
    {name: '中大型货车', color: '#f759ab'},
    {name: '特种车', color: '#fa541c'},
    {name: '非机动车', color: '#1677ff'},
    {name: '行人', color: '#722ed1'},
    {name: '其他', color: '#fa8c16'}
  ],

  // 脱离时主要交通设施
  facilityType: [
    {name: '锥桶', color: '#52c41a'},
    {name: '隔离护栏', color: '#ff4d4f'},
    {name: '施工带', color: '#faad14'},
    {name: '其他', color: '#1890ff'}
  ],

  // 脱离类别
  departureType: [
    {name: '主动脱离', color: '#13c2c2'},
    {name: '被动脱离', color: '#eb2f96'},
    {name: '其他', color: '#fa8c16'}
  ],

  // 脱离原因
  departureReason: [
    {name: '系统故障', color: '#a0d911'},
    {name: '策略缺陷', color: '#f759ab'},
    {name: '人工安全防御', color: '#fa541c'},
    {name: '人为接管', color: '#1677ff'},
    {name: '其他', color: '#fa8c16'}
  ],

  // 人工介入方式
  interventionType: [
    {name: '自动踏板', color: '#52c41a'},
    {name: '方向盘', color: '#ff4d4f'},
    {name: '硬或软开关介入', color: '#faad14'}
  ]
}


// 获取选中分类的标题
const getSelectedCategoryTitle = () => {
  const titles = {
    departureType: '脱离类别',
    sceneCategory: '脱离时场景分类',
    participantType: '交通参与者类型',
    facilityType: '主要交通设施',
    departureReason: '脱离原因',
    interventionType: '人工介入方式'
  }
  return titles[selectedCategory.value] || '统计'
}

// 饼状图公共配置
const getPieChartOption = (data, color = ['#52c41a', '#ff4d4f', '#faad14', '#1890ff', '#722ed1', '#fa8c16']) => ({
  tooltip: {
    trigger: 'item',
    formatter: '{a} <br/>{b}: {c} ({d}%)'
  },
  legend: {
    orient: 'vertical',
    left: 10,
    top: 'center',
    data: data.map(item => item.name),
    textStyle: {
      fontSize: 10
    }
  },
  series: [
    {
      name: '统计',
      type: 'pie',
      radius: ['30%', '60%'],
      center: ['65%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 4,
        borderColor: '#fff',
        borderWidth: 2
      },
      label: {
        show: false
      },
      emphasis: {
        label: {
          show: false,
          fontSize: 14,
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: data,
      color: color
    }
  ]
})

// 小型饼状图配置（适用于所有图表区域）
const getSmallPieChartOption = (data, color) => ({
  tooltip: {
    trigger: 'item',
    formatter: '{a} <br/>{b}: {c} ({d}%)'
  },
  legend: {
    orient: 'vertical',
    left: 'left',
    top: 'center',
    data: data.map(item => item.name),
    textStyle: {
      fontSize: 9
    }
  },
  series: [
    {
      name: '统计',
      type: 'pie',
      radius: ['40%', '70%'],
      center: ['65%', '50%'],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 4,
        borderColor: '#fff',
        borderWidth: 1
      },
      label: {
        show: false
      },
      emphasis: {
        label: {
          show: false,
          fontSize: 12,
          fontWeight: 'bold'
        }
      },
      labelLine: {
        show: false
      },
      data: data,
      color: color
    }
  ]
})

// 初始化地图
const initMap = async () => {
  try {
    await nextTick()

    const AMap = await AMapLoader.load({
      key: '6588d9e5b6ae0751c799cb4c7b3e30be',
      version: '2.0',
      plugins: ['AMap.ToolBar']
    })

    // 创建地图实例
    mapInstance.value = new AMap.Map('departure-map-container', {
      zoom: 12,
      mapStyle: 'amap://styles/45311ae996a8bea0da10ad5151f72979', // 使用项目中已有的地图样式
      center: [103.726465, 36.107992] // 默认中心点（兰州）
    })

    mapInstance.value.addControl(new AMap.ToolBar())

    // 添加点位标记
    addDeparturePoints()
  } catch (error) {
    console.error('地图加载失败:', error)
  }
}

// 添加脱离点位到地图 - 修改为根据选中分类着色
const addDeparturePoints = () => {
  if (!mapInstance.value) return

  // 清除现有标记
  if (markers.value.length > 0) {
    mapInstance.value.remove(markers.value)
    markers.value = []
  }

  // 添加新标记
  departurePoints.value.forEach(point => {
    // 获取对应的颜色
    let pointColor = '#ff4d4f' // 默认颜色
    let pointName = point[selectedCategory.value]
    const categoryColors = chartColors[selectedCategory.value]

    if (categoryColors) {
      // 精确匹配点位的分类属性和饼图数据项的名称，设置对应的颜色
      const matchedColorItem = categoryColors.find((c) => c.name === pointName)
      if (matchedColorItem && matchedColorItem.color) {
        pointColor = matchedColorItem.color
      }
    }

    // 创建自定义标记点样式
    const marker = new window.AMap.Marker({
      position: [point.lng, point.lat],
      icon: new window.AMap.Icon({
        // 使用pointColor变量
        image: `data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D'http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg'%20width%3D'24'%20height%3D'24'%20viewBox%3D'0%200%2024%2024'%3E%3Ccircle%20cx%3D'12'%20cy%3D'12'%20r%3D'6'%20fill%3D'%23${pointColor.replace('#', '')}'%20stroke%3D'%23fff'%20stroke-width%3D'2'/%3E%3C%2Fsvg%3E`,
        size: new window.AMap.Size(16, 16),
        imageSize: new window.AMap.Size(16, 16)
      }),
      offset: new window.AMap.Pixel(-6, -6),
      label: {
        show: false // 确保初始状态下标签不显示
      }
    })

    // 添加鼠标悬浮事件
    marker.on('mouseover', function() {
      this.setLabel({
        content: `<div style="padding: 8px 12px; background-color: white; color: black; border-radius: 6px; font-size: 12px; line-height: 1.5; width: 150px; border: 1px solid #e8e8e8;">
          <div style="margin-bottom: 4px;">${point.licensePlate}</div>
          <div style="margin-bottom: 4px;">${point.time}</div>
          <div style="color: #4096ff; cursor: pointer; text-decoration: underline; text-align: center;" onclick="event.stopPropagation(); window.open('/reportManage/disengagement?id=${point.reportId}', '_blank');">查看详情</div>
        </div>`,
        direction: 'top',
        offset: new window.AMap.Pixel(0, -30)
      })
    })

    marker.on('mouseout', function() {
      this.setLabel({
        content: '',
        show: false
      })
    })

    markers.value.push(marker)
  })

  // 将所有标记添加到地图上
  mapInstance.value.add(markers.value)

  // 调整地图视野，显示所有标记
  if (markers.value.length > 0) {
    mapInstance.value.setFitView(markers.value)
  }
}

// 初始化饼状图
const initCharts = async () => {
  await nextTick()

  // 初始化选中的饼状图
  if (selectedCategoryChart.value) {
    selectedCategoryChartInstance = echarts.init(selectedCategoryChart.value)
    updateSelectedChart()
  }

  // 初始化所有饼状图
  const chartConfigs = [
    { ref: departureTypeChart, key: 'departureType' },
    { ref: sceneCategoryChart, key: 'sceneCategory' },
    { ref: participantTypeChart, key: 'participantType' },
    { ref: facilityTypeChart, key: 'facilityType' },
    { ref: departureReasonChart, key: 'departureReason' },
    { ref: interventionTypeChart, key: 'interventionType' }
  ]

  chartConfigs.forEach(config => {
    if (config.ref.value) {
      const chartInstance = echarts.init(config.ref.value)
      chartInstances[config.key] = chartInstance

      const data = chartData[config.key]
      const colors = chartColors[config.key].map(c => c.color)
      chartInstance.setOption(getSmallPieChartOption(data, colors))
    }
  })

  // 监听窗口大小变化，自适应图表
  window.addEventListener('resize', handleResize)
}

// 处理窗口大小变化
const handleResize = () => {
  selectedCategoryChartInstance?.resize()
  // 调整所有饼状图大小
  Object.values(chartInstances).forEach(instance => instance.resize())
  // 地图自适应
  if (mapInstance.value) {
    mapInstance.value.resize()
  }
}

// 更新选中的饼状图数据
const updateSelectedChart = () => {
  if (!selectedCategoryChartInstance) return

  let data = []
  let colors = []

  // 如果是综合视图，默认显示脱离类别数据
  data = chartData[selectedCategory.value]
  colors = chartColors[selectedCategory.value].map((c) => c.color)

  selectedCategoryChartInstance.setOption(getPieChartOption(data, colors))
}

// 更新所有饼状图数据
const updateAllCharts = () => {
  Object.keys(chartInstances).forEach(key => {
    const chartInstance = chartInstances[key]
    const data = chartData[key]
    const colors = chartColors[key].map(c => c.color)
    chartInstance.setOption(getSmallPieChartOption(data, colors))
  })
}

// 更新地图点位数据
const updateDeparturePoints = (newPoints) => {
  if (newPoints && newPoints.length > 0) {
    departurePoints.value.splice(0, departurePoints.value.length, ...newPoints)
    if (mapInstance.value) {
      addDeparturePoints()
    }
  }
}

// 获取脱离数据
const fetchDepartureData = async () => {
  // 这里可以根据查询条件获取真实的统计数据和点位数据
  // 示例：假设从API获取数据
  // try {
  //   const res = await getDepartureStats(props.query)
  //   updateSelectedChart(res.data.chartData)
  //   updateAllCharts(res.data.allChartData)
  //   updateDeparturePoints(res.data.pointsData)
  // } catch (error) {
  //   console.error('获取脱离数据失败:', error)
  // }

  // 暂时使用模拟数据
  updateSelectedChart()
  updateAllCharts()
}

// 监听查询参数变化，更新数据
watch(() => props.query, () => {
  fetchDepartureData()
}, { deep: true })

// 监听选中分类变化，更新图表和地图点位颜色
watch(selectedCategory, (newValue) => {
  updateSelectedChart()
  addDeparturePoints() // 重新添加点位以更新颜色
})

// 监听抽屉打开状态，初始化数据
watch(() => props.open, (newOpen) => {
  if (newOpen) {
    nextTick(() => {
      initCharts()
      initMap()
      fetchDepartureData()
    })
  }
})

onMounted(async () => {
  if (props.open) {
    // 初始化图表
    await initCharts()
    // 初始化地图
    await initMap()
    // 获取数据
    await fetchDepartureData()
  }
})

// 组件卸载前清理资源
onBeforeUnmount(() => {
  // 移除事件监听器
  window.removeEventListener('resize', handleResize)

  // 销毁图表实例
  selectedCategoryChartInstance?.dispose()
  Object.values(chartInstances).forEach(instance => instance.dispose())

  // 销毁地图实例
  if (mapInstance.value) {
    mapInstance.value.destroy()
  }
})
</script>

<style lang="less" scoped>
.drawer-content {
  height: 100%;
  overflow: auto;
  padding: 7px;
}

.map-section {
  background-color: #ffffff;
  border-radius: 8px;
  padding: 10px;
  width: 70%;
}

/* 修改为 antd 单选框组的样式 */
.radio-group-container {
  margin-bottom: 10px;
  padding: 8px;
  background-color: #f5f5f5;
  border-radius: 6px;
  overflow-x: auto;
}

.radio-group-container :deep(.ant-radio-button-wrapper) {
  margin-right: 8px;
  border-radius: 4px;
  padding: 0 12px;
  height: 28px;
  font-size: 12px;
}

.section-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

.map-container {
  width: 100%;
  height: 400px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
}

/* 新的主布局 */
.new-main-layout {
  display: flex;
  gap: 10px;
  margin-bottom: 10px;
  align-items: flex-start; /* 确保左侧不随右侧高度变化 */
  height: calc(100vh - 180px);
}

/* 左侧月份统计侧边栏 - 固定高度 */
.monthly-stats-sidebar {
  width: 260px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
  position: relative;
}

.monthly-stats-header {
  padding: 15px;
  border-bottom: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.monthly-stats-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #333;
}

.monthly-stats-list {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  max-height: calc(100% - 100px); /* 确保有足够空间显示分页控件 */
}

/* 分页控件样式 - 固定在列表下方 */
.pagination-controls {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 15px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafafa;
  width: 100%;
  box-sizing: border-box;
}

.pagination-btn {
  padding: 4px 12px;
  border: 1px solid #d9d9d9;
  background-color: white;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.3s;
}

.pagination-btn:hover:not(:disabled) {
  border-color: #1890ff;
  color: #1890ff;
}

.pagination-btn:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

.page-info {
  font-size: 12px;
  color: #666;
  min-width: 60px;
  text-align: center;
}

.month-stat-item {
  padding: 7px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  margin-bottom: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background-color: #fafafa;

  .test-info {
    display: flex;
    flex-direction: row;
    gap: 4px;
    padding: 3px 0;
    font-size: 14px;
    line-height: 1.4;
  }

  .test-entity,
  .test-license {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .info-label {
    color: #666;
    min-width: 60px;
  }

  .info-value {
    color: #333;
    font-weight: 500;
    word-break: break-all;
  }
}

.month-stat-item:hover {
  border-color: #1890ff;
  background-color: #e6f7ff;
}

.month-stat-item.active {
  border-color: #1890ff;
  background-color: #e6f7ff;
  box-shadow: 0 2px 4px rgba(24, 144, 255, 0.2);
}

.month-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.month-year {
  font-size: 14px;
  font-weight: 500;
  color: #333;
}

.departure-count {
  font-size: 14px;
  font-weight: bold;
  color: #ff4d4f;
}

.progress-bar {
  width: 100%;
  height: 6px;
  background-color: #f0f0f0;
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background-color: #1890ff;
  transition: width 0.3s ease;
}

/* 右侧内容区域 */
.content-section {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* 主区域布局 */
.main-section {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 10px;
  flex: 1;
  overflow: hidden;
}

/* 右侧区域样式 */
.right-section {
  width: 30%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 总数统计样式 */
.total-stats {
  background-color: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  display: flex;
  justify-content: center;
  align-items: center;
}

.total-count {
  text-align: center;
}

.count-number {
  display: block;
  font-size: 32px;
  font-weight: bold;
  color: #ff4d4f;
  margin-bottom: 8px;
}

.count-label {
  display: block;
  font-size: 14px;
  color: #666;
}

.chart-item-large {
  background-color: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.chart-item-large .chart {
  flex: 1;
  min-height: 200px;
}

.chart-title {
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0;
  color: #333;
  text-align: center;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 10px;
}

.chart {
  width: 100%;
  height: 200px;
}

/* 所有饼状图区域样式 */
.all-charts-section {
  margin-top: 10px;
  background-color: white;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  flex-shrink: 0;
}

.all-charts-title {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
}

/* 饼状图网格布局 - 每行三个 */
.charts-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
}

/* 小型饼状图项样式 */
.chart-item-small {
  background-color: #fafafa;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  display: flex;
  flex-direction: column;
  height: 220px;
}

.chart-item-small .chart {
  flex: 1;
  min-height: 150px;
}

.chart-title-small {
  font-size: 12px;
  font-weight: 600;
  margin: 8px 0 0 0;
  color: #333;
  text-align: center;
}

// 响应式布局调整
@media (max-width: 1400px) {
  .main-section {
    flex-direction: column;
  }

  .map-section {
    width: 100%;
  }

  .right-section {
    width: 100%;
  }
}

@media (max-width: 1200px) {
  .new-main-layout {
    flex-direction: column;
  }

  .monthly-stats-sidebar {
    width: 100%;
    height: auto; /* 在中等屏幕下自动高度 */
    max-height: 400px;
  }

  .monthly-stats-list {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    max-height: 280px;
    overflow-y: auto;
  }

  .month-stat-item {
    flex: 0 0 calc(33.333% - 6px);
    margin-bottom: 0;
  }

  .main-section {
    flex-direction: column;
  }

  .map-section {
    width: 100%;
  }

  .right-section {
    width: 100%;
  }

  /* 调整饼状图网格布局为每行两个 */
  .charts-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 992px) {
  .map-container {
    height: 300px;
  }

  .chart-item-large .chart {
    min-height: 150px;
  }

  .month-stat-item {
    flex: 0 0 calc(50% - 4px);
  }

  .monthly-stats-sidebar {
    height: auto;
  }
}

@media (max-width: 768px) {
  .map-container {
    height: 250px;
  }

  .count-number {
    font-size: 24px;
  }

  .month-stat-item {
    flex: 0 0 100%;
  }

  .monthly-stats-sidebar {
    height: auto;
  }

  /* 调整饼状图网格布局为每行一个 */
  .charts-grid {
    grid-template-columns: 1fr;
  }
}

/* 隐藏高德地图logo和版权信息 */
:deep(.amap-logo) {
  display: none !important;
}

:deep(.amap-copyright) {
  opacity: 0 !important;
}
</style>